import { StyleSheet, View } from "react-native";
import {
  File,
  FileStatus,
  StoragePathType,
  ExternalSourceChunkingStrategyInput,
} from "@axio/graphql/generated/graphql";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { ResourceType } from "../../../contexts/ActiveCourseContext";
import ResourceCard from "./ResourceCard";
import { ContentPicker } from "../../common/general/ContentPicker/ContentPicker";
import { ExternalPickedItem, PickedItem } from "../../common/general/ContentPicker/ContentPicker.types";
import { useAttachExternalContent } from "../../../hooks/contentPicker/useAttachExternalContent";

import { STUDENT_ASSIGNMENT_CONTEXT } from "../../../utils/contentPicker/contentPickerContextPresets";
import { useExternalIngestionStatus } from "../../../hooks/contentPicker/useExternalIngestionStatus";
import {
  convertExternalPickedItemsToAttachmentInput,
  pairExternalItemsWithQueuedJobs,
} from "../../../utils/contentPicker/externalItemAdapter";

interface StudentFileUploadProps {
  resource: { resourceType: ResourceType; resourceId: string };
  filesState: [Partial<File>[], Dispatch<SetStateAction<Partial<File>[]>>];
  storagePath: StoragePathType;
  onSubmitFiles: () => Promise<void>;
}

// Extended file type to track external ingestion
interface ExtendedFile extends Partial<File> {
  isExternalIngesting?: boolean;
  externalJobId?: string;
}

const StudentFileUpload = ({ resource, filesState, storagePath, onSubmitFiles }: StudentFileUploadProps) => {
  const [pendingSubmission, setPendingSubmission] = useState(false);
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);
  const [files, setFiles] = filesState as [ExtendedFile[], Dispatch<SetStateAction<ExtendedFile[]>>];

  const [externalJobIds, setExternalJobIds] = useState<string[]>([]);
  const prevPickedRef = useRef<PickedItem[]>([]);

  // Hooks for external content
  const { attachExternal, loading: attachLoading, error: attachError } = useAttachExternalContent();
  const { jobStatuses, isPolling } = useExternalIngestionStatus(externalJobIds);

  useEffect(() => {
    setFileUploadError(null);
  }, [resource?.resourceId]);

  const handleDeleteFile = (fileId: string) => {
    setFiles((prev) => prev.filter((file) => file.id !== fileId));
  };

  const handleSubmitFiles = async () => {
    try {
      await onSubmitFiles();
      setPendingSubmission(false);
    } catch (error) {
      console.error("Error submitting files:", error);
    }
  };

  // Handle items selected from ContentPicker (local uploads + external)
  const handleFilesSelected = useCallback(
    async (items: PickedItem[]) => {
      setFileUploadError(null);

      // Determine newly added items by comparing with previous selection
      const prevItems = prevPickedRef.current;
      const newItems = items.slice(prevItems.length);
      prevPickedRef.current = items;

      if (newItems.length === 0) return;

      // Process local uploads - already uploaded by ContentPicker when storagePath is set
      const localItems = newItems.filter((item) => item.kind === "local");
      if (localItems.length > 0) {
        const newFiles: ExtendedFile[] = localItems.map((item) => ({
          id: item.uploadedFileId ?? `local-${Date.now()}-${Math.random()}`,
          fileName: item.name,
          fileURL: item?.url ?? "",
          created: new Date(),
          status: FileStatus.Pending,
        }));
        setFiles((prev) => [...prev, ...newFiles]);
      }

      // Process external items via attach mutation
      const externalItems = newItems.filter((item): item is ExternalPickedItem => item.kind === "external");
      if (externalItems.length > 0) {
        try {
          const { grouped, rejected } = convertExternalPickedItemsToAttachmentInput(externalItems);

          if (rejected.length > 0) {
            const rejectedNames = rejected.map((r) => r.item.name).join(", ");
            setFileUploadError(`Some items could not be added: ${rejectedNames}`);
          }

          const allJobIds: string[] = [];
          for (const group of grouped) {
            const result = await attachExternal({
              connectorType: group.connectorType,
              externalItemIds: group.externalItemIds,
              strategy: ExternalSourceChunkingStrategyInput.RecursiveTextSplitting,
            });

            const itemJobPairs = pairExternalItemsWithQueuedJobs(
              group.items,
              result?.attachExternalContent?.data?.itemResults ?? []
            );
            allJobIds.push(...itemJobPairs.map(({ jobId }) => jobId));

            const newFiles: ExtendedFile[] = itemJobPairs.map(({ item, jobId }, index) => ({
              id: `external-${item.externalId}-${jobId}-${index}`,
              fileName: item.name,
              fileURL: item.url ?? "",
              created: new Date(),
              status: FileStatus.Pending,
              isExternalIngesting: true,
              externalJobId: jobId,
            }));

            setFiles((prev) => [...prev, ...newFiles]);
          }

          setExternalJobIds((prev) => [...prev, ...allJobIds]);
        } catch (error) {
          console.error("Error importing external items:", error);
          setFileUploadError("Failed to import selected items. Please try again.");
        }
      }
    },
    [attachExternal, setFiles, onSubmitFiles]
  );

  // Update file statuses based on job polling
  useEffect(() => {
    if (jobStatuses.size === 0) return;

    const ingestionErrorMessages: string[] = [];

    setFiles((prev) => {
      return prev.flatMap((file) => {
        // Skip files without external job tracking
        if (!file.externalJobId || !file.isExternalIngesting) {
          return [file];
        }

        // Check if this file's job has completed
        const jobStatus = jobStatuses.get(file.externalJobId);
        if (!jobStatus || !jobStatus.isTerminal) {
          return [file];
        }

        // Update with real file data from successful ingestion
        if (jobStatus.category === "completed" && jobStatus.file?.id) {
          return [
            {
              id: jobStatus.file.id,
              fileName: jobStatus.file.fileName,
              fileURL: jobStatus.file.fileURL,
              created: new Date(jobStatus.file.created || Date.now()),
              status: FileStatus.Pending,
              isExternalIngesting: false,
              externalJobId: undefined,
            },
          ];
        }

        if (jobStatus.category === "completed" && !jobStatus.file?.id) {
          const fileLabel = file.fileName || "This file";
          ingestionErrorMessages.push(`${fileLabel} could not be processed: no uploaded file was returned.`);
          return [];
        }

        if (jobStatus.category === "completed" && !jobStatus.file?.id) {
          return {
            ...file,
            status: FileStatus.Rejected,
            isExternalIngesting: false,
            externalJobId: undefined,
          };
        }

        // Handle failed ingestion
        if (jobStatus.category === "failed") {
          const fileLabel = file.fileName || "This file";
          const reason = jobStatus.failedReason?.trim();
          const message = reason
            ? `${fileLabel} could not be processed: ${reason}`
            : `${fileLabel} could not be processed.`;
          ingestionErrorMessages.push(message);
          return [];
        }

        return [file];
      });
    });

    if (ingestionErrorMessages.length > 0) {
      setFileUploadError(ingestionErrorMessages.join(" "));
    }
  }, [jobStatuses, setFiles]);

  return (
    <>
      <ContentPicker
        onFilesSelected={handleFilesSelected}
        multiSelect={true}
        storagePath={storagePath}
        isSelectingList={false}
        isPublic={false}
        allowLinks={false}
        context={STUDENT_ASSIGNMENT_CONTEXT}
      />

      {fileUploadError && (
        <View role="alert" accessibilityLiveRegion="assertive" style={{ marginTop: 8 }}>
          <CustomText text={fileUploadError} size="xs" style={{ color: "#E53E3E" }} useTranslationText={false} />
        </View>
      )}

      {attachError && (
        <View role="alert" accessibilityLiveRegion="assertive" style={{ marginTop: 8 }}>
          <CustomText
            text={`Error attaching external content: ${attachError.message}`}
            size="xs"
            style={{ color: "#E53E3E" }}
            useTranslationText={false}
          />
        </View>
      )}

      <CustomSpacing type="vertical" size="m" />

      {files.length > 0 && files.some((file) => file.status === FileStatus.Pending) && (
        <>
          <View style={styles.filesContainer}>
            {files
              .filter((file) => file.status === FileStatus.Pending)
              .map((file) => {
                const jobStatus = file.externalJobId ? jobStatuses.get(file.externalJobId) : null;
                const ingestionStatus = file.isExternalIngesting
                  ? jobStatus?.category === "failed"
                    ? "failed"
                    : "ingesting"
                  : undefined;

                return (
                  <ResourceCard
                    key={file.id}
                    file={file}
                    showDate={false}
                    showDelete={true}
                    onDelete={() => file.id && handleDeleteFile(file.id)}
                    ingestionStatus={ingestionStatus}
                  />
                );
              })}
          </View>
          <CustomSpacing type="vertical" size="m" />
        </>
      )}

      {/* Only show submit button if there are files */}
      {files.length > 0 && files.some((file) => file.status === FileStatus.Pending) && (
        <>
          {resource.resourceType === ResourceType.ACTIVITY_ASSIGNMENT && (
            <>
              <CustomText
                text="You can submit your assignment once. You cannot change your submission once submitted."
                size="s"
                style={styles.submissionInfoText}
                useTranslationText={false}
              />

              <CustomSpacing type="vertical" size="m" />
            </>
          )}

          {isPolling && (
            <>
              <CustomText
                text="Some files are still being processed. Please wait before submitting."
                size="xs"
                style={{ color: "#F59E0B", marginBottom: 8 }}
                useTranslationText={false}
              />
            </>
          )}

          <CustomButton
            disabled={pendingSubmission || isPolling || attachLoading}
            text={resource.resourceType === ResourceType.ACTIVITY_PROJECT ? "Submit Project" : "Submit Assignment"}
            size="m"
            onPress={handleSubmitFiles}
            style={pendingSubmission || isPolling || attachLoading ? styles.disabledButton : styles.submitButton}
            textStyle={pendingSubmission || isPolling || attachLoading ? { color: "#475467" } : { color: "#FFFFFF" }}
            leftIcon={undefined}
            rightIcon={undefined}
          />
        </>
      )}
    </>
  );
};

export default StudentFileUpload;

const styles = StyleSheet.create({
  submissionInfoText: {
    color: "#6B7280",
  },
  submitButton: {
    backgroundColor: "#3E68FE",
    width: "100%",
  },
  disabledButton: {
    backgroundColor: "#EAECF0",
    width: "100%",
  },
  filesContainer: {
    marginBottom: 10,
    flexDirection: "column",
    gap: 10,
    width: "100%",
  },
});
